import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import type { User, TeamPermissions, TeamRole } from "../types";
import { profileToUser } from "../types";
import type { ProfileRow } from "../types";

const MAX_MEMBERS = 2;

export function useTeam(ownerId: string | undefined) {
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!ownerId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from("profiles")
        .select("*")
        .or(`id.eq.${ownerId},owner_id.eq.${ownerId}`)
        .eq("role", "admin")
        .order("is_owner", { ascending: false });

      if (e) {
        setError(e.message);
        setMembers([]);
        return;
      }
      const rows = (data ?? []) as ProfileRow[];
      setMembers(rows.map(profileToUser));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement équipe");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const addMember = useCallback(
    async (
      data: {
        name: string;
        email: string;
        team_role: TeamRole;
        permissions?: TeamPermissions;
      }
    ): Promise<{ error: string | null }> => {
      if (!ownerId) return { error: "Non autorisé" };
      try {
        // Ne compter que les membres d'équipe (Second Admin / Membre), pas les partenaires ni poseurs
        const { data: existing } = await supabase
          .from("profiles")
          .select("id")
          .eq("owner_id", ownerId)
          .eq("role", "admin");
        if ((existing?.length ?? 0) >= MAX_MEMBERS)
          return {
            error: `Maximum ${MAX_MEMBERS} personnes (Second Admin ou Membre). Retirez quelqu’un pour en ajouter un autre.`,
          };

        // Rafraîchir la session pour avoir un token valide (évite 401 à l'appel de la fonction)
        const { data: sessionData, error: sessionError } =
          await supabase.auth.refreshSession();
        if (sessionError || !sessionData?.session) {
          return {
            error:
              "Session expirée ou invalide. Déconnectez-vous, reconnectez-vous, puis réessayez.",
          };
        }

        // Appel à l'Edge Function : Supabase envoie l'email d'invitation (lien pour définir le mot de passe).
        const redirectTo =
          typeof window !== "undefined" ? `${window.location.origin}/` : undefined;
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          "invite-team-member",
          {
            body: {
              name: data.name,
              email: data.email,
              team_role: data.team_role,
              permissions: data.permissions ?? {},
              owner_id: ownerId,
              redirect_to: redirectTo,
            },
          }
        );

        if (fnError) {
          const raw = fnError.message;
          const msg =
            raw === "Failed to send a request to the Edge Function"
              ? "Impossible de joindre la fonction. Vérifiez la connexion et que le projet Supabase n'est pas en pause."
              : raw === "Edge Function returned a non-2xx status code"
                ? "Session expirée ou invalide. Déconnectez-vous, reconnectez-vous, puis réessayez."
                : raw;
          return { error: msg };
        }
        const err = (fnData as { error?: string })?.error;
        if (err) return { error: err };
        await refetch();
        return { error: null };
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Erreur invitation",
        };
      }
    },
    [ownerId, refetch]
  );

  const updatePermissions = useCallback(
    async (
      memberId: string,
      permissions: TeamPermissions
    ): Promise<{ error: string | null }> => {
      try {
        const { error: e } = await supabase
          .from("profiles")
          .update({ permissions, updated_at: new Date().toISOString() })
          .eq("id", memberId)
          .eq("owner_id", ownerId);
        if (e) return { error: e.message };
        await refetch();
        return { error: null };
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Erreur mise à jour",
        };
      }
    },
    [ownerId, refetch]
  );

  const removeMember = useCallback(
    async (memberId: string): Promise<{ error: string | null }> => {
      if (!ownerId) return { error: "Non autorisé" };
      try {
        const { data: sessionData, error: sessionError } =
          await supabase.auth.refreshSession();
        if (sessionError || !sessionData?.session) {
          return {
            error:
              "Session expirée ou invalide. Déconnectez-vous, reconnectez-vous, puis réessayez.",
          };
        }
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          "remove-team-member",
          {
            body: { member_id: memberId, owner_id: ownerId },
          }
        );
        if (fnError) {
          const msg =
            fnError.message === "Failed to send a request to the Edge Function"
              ? "Impossible de joindre la fonction. Vérifiez la connexion."
              : fnError.message === "Edge Function returned a non-2xx status code"
                ? "Session expirée ou invalide. Déconnectez-vous et reconnectez-vous puis réessayez."
                : fnError.message;
          return { error: msg };
        }
        const err = (fnData as { error?: string })?.error;
        if (err) return { error: err };
        await refetch();
        return { error: null };
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Erreur suppression",
        };
      }
    },
    [ownerId, refetch]
  );

  return {
    members,
    loading,
    error,
    refetch,
    addMember,
    updatePermissions,
    removeMember,
    canAddMore: members.filter((m) => m.id !== ownerId).length < MAX_MEMBERS,
  };
}
